import { useEffect } from "react"
import { NextSeo } from "next-seo"
import { useRouter } from "next/router"
import {
	metadata,
	SVOD_BLOCK_TYPE,
	SVOD_TYPE_HEADER,
	SVOD_TYPE_SPOTLIGHT,
	SVOD_TYPE_TEMATICS,
	SVOD_TYPE_SQUARE,
	SVOD_TYPE_SLIDER
} from "lacinetek-common/src/metadata"

import { FETCH_SVOD_CATEGORIES } from "lib/graphql/queries"
import { useCountry } from "lib/country"
import Gtm from "lib/gtm"
import { useTranslation } from "lib/i18n/translation"

import { Query } from "components/common"
import SkeletonLink from "components/common/skeleton-link"
import { HeaderSubscription } from "components/subscription/header"
import { SpotlightFilm } from "components/subscription/spotlight-film"
import { Thematics } from "components/subscription/thematics"
import { Square } from "components/subscription/square"
import { SliderSubscription } from "components/subscription/slider"
import { PartnerBanner } from "components/subscription/partner"

const Subscription = ({ query }) => {
	const { country: { isoCode }, asPath } = useCountry()
	const { c } = useTranslation()
	const router = useRouter()
	const url = router ? router.asPath : null
	const hash = url.split("#")[1]

	useEffect(() => {
		if(!hash) {
			return
		}

		let counter = 0
		const interval = setInterval(() => {
			counter++

			if (counter > 60) {
				clearInterval(interval)
			}

			const element = document.getElementById(hash)

			if (element) {
				element.scrollIntoView()
				clearInterval(interval)
			}
		}, 500)

		return () => {
			clearInterval(interval)
		}
	}, [hash])

	return (
		<Query
			query={FETCH_SVOD_CATEGORIES}
			options={{ variables: { query: `abonnement-${isoCode}` } }}
		>
			{({ cms: { categories: { items } } }) => {

				return items.length > 0
					? <>
						<Gtm pageType="Page abonnement" pagePath={url} pageTitle={c("lacinetek-subscription")} />
						<NextSeo
							title={c("lacinetek-subscription")}
							description={c("offer-subscription-description")}
							canonical={asPath}
							openGraph={{
								title: c("lacinetek-subscription"),
								description: c("offer-subscription-description"),
								url: asPath
							}}
							/>
						<PartnerBanner query={query} />
						{
							items[0].children.items.map((block, index) => blockSelection(block, index, query))
						}
					</>
					: <section className="centered">
						<p>
							{c("available-soon")}
						</p>
						<p className="section-link">
							<SkeletonLink href="/">
									<a title={c("back-homepage")} className="icon-left button">{c("back-homepage")}</a>
							</SkeletonLink>
						</p>
					</section>
			}}
		</Query>
	)
}

const blockSelection = (block, index, query) => {
	let Component = null
	const type = metadata(block, SVOD_BLOCK_TYPE)
	const componentKey = `svod-${type}-${index}`
	const className = index % 2 ? undefined : "grey"
	const overlayAuto = block.linkRewrite === query.slug

	switch (type) {
		case SVOD_TYPE_HEADER:
			Component = <HeaderSubscription key={componentKey} block={block} gtmAction={true} />
			break
		case SVOD_TYPE_SPOTLIGHT:
			Component = <SpotlightFilm
				key={componentKey}
				block={block}
				className={className}
				overlayAuto={overlayAuto}
			/>
			break
		case SVOD_TYPE_TEMATICS:
			Component = <Thematics
				key={componentKey}
				block={block}
				className={className}
				overlayAuto={overlayAuto}
			/>
			break
		case SVOD_TYPE_SQUARE:
			Component = <Square
				key={componentKey}
				block={block}
				className={className}
				overlayAuto={overlayAuto}
			/>
			break
		case SVOD_TYPE_SLIDER:
			Component = <SliderSubscription
				key={componentKey}
				block={block}
				className={className}
			/>
			break
	}

	return Component
}

Subscription.getInitialProps = async (ctx) => {
	const query = ctx.query

	return {
		query
	}
}

export default Subscription
